// Application Components
import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { object, string } from 'yup'
import { translateField } from '../../functions/field-functions'
import { extractSubmissionForm } from '../../functions/form-functions'

//// Application Slices
import { selectSettings } from '../../reducers/settingSlice'
import {
    updateFormData,
    updateFormValidity,
    updateFormSystem,
    selectForms,
} from '../../reducers/formSlice'

//// Application Components
import DividerComponent from '../../components/simple-components/divider-component'
import ButtonComponent from '../../components/conditional-components/button-component'
import LoadingComponent from '../../components/simple-components/loading-component'
import NotificationComponent from '../../components/conditional-components/notification-component'

//// Carbon Components
import { Form, Select, SelectItem } from '@carbon/react'

//// Carbon Icons

//// Static Constants

//////// Component
const RegisteredNursingExaminationGetStartedForm = ({ instance }) => {
    //////// Hooks
    const dispatch = useDispatch()
    const navigate = useNavigate()

    //////// State
    const [loading, setLoading] = useState(false)
    const [notification, setNotification] = useState({})

    //////// Slices
    const settingsData = useSelector(selectSettings)
    const formsData = useSelector(selectForms)

    //// Slice Data
    const digital_name = settingsData?.['form']?.['digital_name']
    const submit_route = settingsData?.['form']?.['submit_route']
    const fields = settingsData?.['form']?.['fields']

    //////// Search Params

    //////// Methods

    // Validate Data Submission
    const validation = async (submit) => {
        // Validate Form Submission Data Against Schema
        try {
            // Schema Object
            const schema = {}
            schema.yup = object({
                GetStar: string().required(
                    'This is a required field. Please make a selection in order to save and continue.'
                ),
                GetStarTwo: string().required(
                    'This is a required field. Please make a selection in order to save and continue.'
                ),
            })
            schema.valid = {
                GetStar: false,
                GetStarErrMsg: '',
                GetStarTwo: false,
                GetStarTwoErrMsg: '',
            }
            await schema.yup.validate(
                { ...submit },
                { strict: true, abortEarly: false }
            )
            dispatch(
                updateFormValidity({
                    digital_name: digital_name,
                    errors: {},
                })
            )
            return true
        } catch (err) {
            const errors = {}
            if (err?.inner?.length > 0) {
                await err.inner.forEach(async (el) => {
                    if (el?.path) {
                        errors[el?.path] = true
                        if (el?.errors?.length > 0) {
                            const errName = el?.path + 'ErrMsg'
                            const errMsg = el?.errors?.join(' ')
                            const tran = translateField(fields, el?.path)
                            if (tran) {
                                const tranErrMsg = await errMsg.replaceAll(
                                    el?.path,
                                    tran
                                )
                                errors[errName] = tranErrMsg
                            } else {
                                errors[errName] = el?.errors?.join(' ')
                            }
                        }
                    }
                })
            }
            //// Start Watson Assistant Journey

            //// End Watson Assistant Journey
            dispatch(
                updateFormValidity({
                    digital_name: digital_name,
                    errors: errors,
                })
            )
            return false
        }
    }

    // Re-Validate Data Submission
    const revalidation = async (event_name) => {
        try {
            // Update Validation State
            if (digital_name && event_name) {
                dispatch(
                    updateFormValidity({
                        digital_name: digital_name,
                        errors: {
                            [event_name]: false,
                            [event_name + 'ErrMsg']: '',
                        },
                    })
                )
            }
        } catch (error) {
            console.warn('Revalidation Error:', error)
            return null
        }
    }

    //////// Functional Methods

    // Handle Data Of All Fields
    const handleData = (e) => {
        if (e?.target?.name && e?.target?.value) {
            // console.log("pain point get started test", e.target.name, e.target.value)
            dispatch(
                updateFormData({
                    digital_name: digital_name,
                    values: {
                        [e.target.name]: e.target.value,
                    },
                })
            )
            revalidation(e.target.name)
        } else if (e?.target?.name) {
            dispatch(
                updateFormData({
                    digital_name: digital_name,
                    values: { [e.target.name]: '' },
                })
            )
            revalidation(e.target.name)
        }
        //// Start Watson Assistant Journey

        if (e?.target?.name === 'GetStar' && e?.target?.value === 'Y') {
            instance.tours.startTour(
                'Registered Nursing Endorsement Get Started Multi State Yes Tour'
            )
        } else if (e?.target?.name === 'GetStar' && e?.target?.value === 'N') {
            instance.tours.startTour(
                'Registered Nursing Endorsement Get Started Multi State No Tour'
            )
        } else if (
            e?.target?.name === 'GetStarTwo' &&
            e?.target?.value === 'Y'
        ) {
            instance.tours.startTour(
                'Registered Nursing Endorsement Get Started Military Waiver Yes Tour'
            )
        } else {
            instance.tours.endTour()
        }

        //// End Watson Assistant Journey
    }

    // Handle Update Of Conditional Form Validity
    const handleConditionalFormsValidationRequirements = () => {
        // Check For Get Started First Question (Dependant Form)
        if (formsData?.[digital_name]?.['values']?.['GetStar'] === 'N') {
            dispatch(
                updateFormSystem({
                    digital_name: 'nurse-licensure-compact',
                    system: { valid: true },
                })
            )
        }

        // Check For Get Started Second Question (Dependant Form)
        if (formsData?.[digital_name]?.['values']?.['GetStarTwo'] === 'N') {
            dispatch(
                updateFormSystem({
                    digital_name: 'military-fee-waiver',
                    system: { valid: true },
                })
            )
        }
    }

    // Handle Form Submission
    const handleSubmit = async () => {
        try {
            console.info('Get Started: Submitted')

            setLoading(true)

            // Extract Form Data From Redux
            const submit = await extractSubmissionForm(digital_name, formsData)

            // Validate Form Submission Data
            const valid = await validation(submit)

            // Check If Form Data Is Valid
            if (valid) {
                console.info('Get Started: Passed Valid')

                handleConditionalFormsValidationRequirements()
                dispatch(
                    updateFormSystem({
                        digital_name: digital_name,
                        system: { valid: true },
                    })
                )
                setLoading(false)
                setNotification({
                    type: 'inline',
                    visible: false,
                    kind: '',
                    title: '',
                    subtitle: '',
                })

                // Navigate To Next Form
                navigate(submit_route ? submit_route : '/')
            } else {
                console.info('Get Started: Failed Valid')

                dispatch(
                    updateFormSystem({
                        digital_name: digital_name,
                        system: { valid: false },
                    })
                )
                setLoading(false)
                setNotification({
                    type: 'inline',
                    visible: true,
                    kind: 'error',
                    title: 'Form Errors:',
                    subtitle:
                        'There are errors on the form (such as missing required info). Please scroll up and update the fields with red messages.',
                })

                console.log(
                    'Get Started Failed Valid: There are errors on the form (such as missing required info). Please scroll up and update the fields with red messages.'
                )
            }
        } catch (error) {
            console.warn('Handle Submit Error:', error)
            return null
        }
    }

    // Handle Keyboard Enter As Submit (Method)
    const handleKeyDown = async (event) => {
        try {
            if (event?.key === 'Enter') {
                await handleSubmit()
            }
        } catch (error) {
            console.warn('Handle Down Key Error:', error)
            return null
        }
    }

    //////// Memoization

    //////// Effects/Side Effects

    // Reload Watson Assistant Journey on Reload
    useEffect(() => {
        if (instance?.tours) {
            instance.tours.endTour()
        }
        return () => {}
    }, [instance])

    //////////////// Render & Return
    if (loading) {
        return (
            <>
                <Form
                    tabIndex={0}
                    onKeyDown={handleKeyDown}
                    onSubmit={handleSubmit}
                >
                    <LoadingComponent />
                </Form>
            </>
        )
    } else {
        return (
            <>
                <Form
                    tabIndex={0}
                    onKeyDown={handleKeyDown}
                    onSubmit={handleSubmit}
                >
                    <p>
                        <strong>Military Veteran Fee Waiver Program</strong>
                    </p>

                    <p>
                        Per section 456.013, Florida Statutes, the department
                        shall waive the initial licensing fee, the initial
                        application fee, and the initial unlicensed activity fee
                        for military veterans and the spouse of military
                        veterans who apply for an initial license. The
                        application must be{' '}
                        <u>
                            submitted within 60 months after an honorable
                            discharge
                        </u>{' '}
                        from any branch of the United States Armed Forces.
                    </p>

                    <Select
                        className="required"
                        defaultValue={
                            formsData?.[digital_name]?.values?.GetStarTwo
                        }
                        value={formsData?.[digital_name]?.values?.GetStarTwo}
                        helperText=""
                        labelText="Military Veteran / Spouse Fee Waiver - I have been honorably discharged from a branch of the United States Armed Forces within the previous 60 months OR I am the spouse of a veteran who has been honorably discharged from a branch of the United States Armed Forces within the previous 60 months. I understand that any student loan forgiveness fee is not part of this fee waiver."
                        name="GetStarTwo"
                        id="GetStarTwo"
                        onChange={handleData}
                        invalid={formsData?.[digital_name]?.errors?.GetStarTwo}
                        invalidText={
                            formsData?.[digital_name]?.errors?.GetStarTwoErrMsg
                        }
                    >
                        <SelectItem text="Choose an option" value="" />
                        <SelectItem text="Yes" value="Y" />
                        <SelectItem text="No" value="N" />
                    </Select>

                    <DividerComponent
                        text="Requirements that must be met to qualify for a
                            multistate license from Florida"
                    />

                    <ol>
                        <li>Florida must be the Primary State of Residence*</li>
                        <li>Must have passed the NCLEX or the SBTPE</li>
                        <li>
                            {`Florida's`} requirements for initial licensure
                            must be met
                        </li>
                        <li>
                            The status of all nursing licenses (CNA, LPN, RN,
                            and APRN) must be clear and unencumbered (in all
                            jurisdictions)**
                        </li>
                        <li>
                            Must not have <strong>any</strong> misdemeanor
                            conviction*** related to the practice of nursing,{' '}
                            <strong>regardless of adjudication</strong>
                        </li>
                        <li>
                            Must not have <strong>any</strong> felony
                            conviction***,{' '}
                            <strong>regardless of adjudication</strong>
                        </li>
                        <li>
                            Must not be enrolled with the Intervention Project
                            for Nurses (IPN) or any other treatment program for
                            impaired practitioners
                        </li>
                        <li>Must have a U.S. Social Security number</li>
                        <li>
                            Must have completed an LPN or RN program, PNEQ{' '}
                            <strong>
                                <u>does not</u>
                            </strong>{' '}
                            qualify
                        </li>
                    </ol>

                    <p>
                        <u>
                            Applicants Educated Outside the U.S. or NCSBN
                            Jurisdiction Only
                        </u>
                    </p>

                    <ol start="10">
                        <li>
                            Education must be evaluated by an independent
                            credentials review agency
                        </li>
                        <li>
                            Education completed in a language other than English
                            will require an English competency examination
                        </li>
                    </ol>

                    <DividerComponent text="Terminology" />

                    <p>
                        *<strong>Primary state of residence</strong> is defined
                        by the Compact as the{' '}
                        {`"person's declared
                    fixed permanent and principal home for legal
                    purposes; domicile."`}
                    </p>

                    <p>
                        **<strong>Encumbrance means</strong> &quot;revocation or
                        suspension of, or any limitation on, the full and
                        unrestricted practice of nursing, imposed by a licensing
                        board.&quot;
                    </p>

                    <p>
                        ***<strong>Conviction</strong> is defined as being
                        &quot;convicted or found guilty, or has entered into an
                        agreed disposition other than a disposition that results
                        in nolle prosequi, for an offense under applicable state
                        or federal criminal law.&quot;
                    </p>

                    <p>
                        Applicants processed for initial licensure who{' '}
                        <strong>also meet the above requirements</strong> for
                        multistate licensure can request a multistate license by
                        answering &quot;Yes&quot; to the question below.
                    </p>

                    <p>
                        If you <strong>do not</strong> wish to be issued a
                        multistate license OR you do not meet the requirements
                        for a multistate license, answer &quot;No&quot; below.
                    </p>

                    <p>
                        <strong>
                            Note: If Florida is NOT your Primary State of
                            Residence and you select &quot;Yes&quot; below to
                            apply for a multistate license, your application may
                            experience significant delays.
                        </strong>
                    </p>

                    <Select
                        className="required"
                        defaultValue={
                            formsData?.[digital_name]?.values?.GetStar
                        }
                        value={formsData?.[digital_name]?.values?.GetStar}
                        helperText=""
                        labelText="Do you wish to apply for a multistate license?
                                If you wish to apply for a single state license
                                OR do not meet the requirements for a multistate
                                license, select &ldquo;No.&rdquo;"
                        name="GetStar"
                        id="GetStar"
                        onChange={handleData}
                        invalid={formsData?.[digital_name]?.errors?.GetStar}
                        invalidText={
                            formsData?.[digital_name]?.errors?.GetStarErrMsg
                        }
                    >
                        <SelectItem text="Choose an option" value="" />
                        <SelectItem text="Yes" value="Y" />
                        <SelectItem text="No" value="N" />
                    </Select>

                    <NotificationComponent notification={notification} />

                    <div className="button-group">
                        <div className="button-group-item">
                            <ButtonComponent
                                click={handleSubmit}
                                title="Save Progress"
                            />
                        </div>
                    </div>
                </Form>
            </>
        )
    }
}

export default RegisteredNursingExaminationGetStartedForm
